import React from 'react';
import PropTypes from 'prop-types';
import { reduxForm } from 'redux-form';
import { storeOrderFormStyles } from './styles';
import { Paper, Tooltip } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Input from '../../shared/components/inputs/Input';
import Textarea from '../../shared/components/inputs/Textarea';
import Picklist from '../../shared/components/inputs/Picklist';
import Checkbox from '../../shared/components/inputs/Checkbox';
import TableInput from '../../shared/components/inputs/Table';
import FormField from '../../shared/components/inputs/FormField';
import Alert from '../../shared/components/Alert';

// Validation function
const validate = (values, props) => {
  const errors = {};
  const { template } = props;

  if (template && template.fields) {
    template.fields.forEach(field => {
      if (field.type === 'note') return; // Skip note fields
      if (field.required && !values[field.name]) {
        errors[field.name] = `${field.label} is required`;
      }
    });
  }

  return errors;
};

// Helper function to generate tooltip message for disabled submit button
const getSubmitButtonTooltip = (invalid, submitting, template) => {
  if (submitting) {
    return 'Submitting order...';
  }
  if (invalid && template && template.fields) {
    const requiredFields = template.fields.filter(field => field.required);
    return `Please fill in required fields: ${requiredFields
      .map(field => field.label)
      .join(', ')}`;
  }

  return '';
};

const renderField = (field, fieldMetadata, classes) => {
  const { type, picklist, columns, initialRows } = field;

  // Handle note type separately - render Alert without FormField wrapper
  if (type === 'note') {
    return (
      <div key={field.name} className={classes.field}>
        <Alert warning={field.help} />
      </div>
    );
  }

  // Create field metadata object for the field
  const metadata = {
    [field.name]: {
      label: field.label,
      required: field.required,
      disabled: field.disabled,
      picklist: picklist,
      columns: columns,
      initialRows: initialRows
    }
  };

  let component;
  switch (type) {
    case 'textarea':
      component = Textarea;
      break;
    case 'picklist':
      component = Picklist;
      break;
    case 'checkbox':
      component = Checkbox;
      break;
    case 'number':
      component = Input;
      break;
    case 'date':
      component = Input;
      break;
    case 'table':
      component = TableInput;
      break;
    case 'text':
    default:
      component = Input;
      break;
  }

  return (
    <div key={field.name} className={classes.field}>
      <FormField
        name={field.name}
        component={component}
        fieldMetadata={metadata}
        type={type === 'number' ? 'number' : type === 'date' ? 'date' : 'text'}
      />
    </div>
  );
};

const StoreOrderForm = ({
  template,
  handleSubmit,
  submitting,
  onCancel,
  pristine,
  invalid,
  error
}) => {
  const classes = storeOrderFormStyles();

  if (!template) {
    return <CircularProgress />;
  }

  return (
    <div className={classes.container}>
      <Paper elevation={3} className={classes.paper}>
        <form onSubmit={handleSubmit} className={classes.formContainer}>
          <h2>{template.name}</h2>
          <div className="order-form">
            {template.fields &&
              template.fields.map(field => renderField(field, {}, classes))}
          </div>
          <Alert error={error} />
          <div className={classes.actionsContainer}>
            <Tooltip
              title={getSubmitButtonTooltip(invalid, submitting, template)}
              placement="top"
              arrow>
              <span>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={submitting || invalid}
                  className={classes.button}>
                  {submitting ? <CircularProgress size={24} /> : 'Submit Order'}
                </Button>
              </span>
            </Tooltip>
            <Button
              type="button"
              variant="outlined"
              onClick={onCancel}
              disabled={submitting}
              className={classes.button}>
              Cancel
            </Button>
          </div>
        </form>
      </Paper>
    </div>
  );
};

StoreOrderForm.propTypes = {
  template: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    fields: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
        required: PropTypes.bool,
        picklist: PropTypes.arrayOf(PropTypes.string),
        disabled: PropTypes.bool,
        columns: PropTypes.arrayOf(
          PropTypes.shape({
            name: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            type: PropTypes.string.isRequired,
            picklist: PropTypes.arrayOf(PropTypes.string)
          })
        ),
        initialRows: PropTypes.arrayOf(PropTypes.object)
      })
    )
  }),
  handleSubmit: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  pristine: PropTypes.bool.isRequired,
  invalid: PropTypes.bool.isRequired,
  error: PropTypes.string,
  values: PropTypes.object
};

export default reduxForm({
  form: 'StoreOrderForm',
  validate,
  enableReinitialize: true
})(StoreOrderForm);
